import React, { useEffect, useState } from "react";
import {
  Card, CardBody, CardHeader, Col, Container, Row, Input, Label, Button,
  Alert, Spinner, Badge, FormGroup,
} from "reactstrap";
import { Link } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ResearchDisclaimer from "../../Components/Common/ResearchDisclaimer";
import RequestAccessButton from "../Subscription/RequestAccessButton";
import SubscribeButton from "../Subscription/SubscribeButton";
import { getProfile, updateDisplayName, syncStoredUser } from "../../services/profileService";
import { displayNameOf, realNameOf } from "../../helpers/displayName";
import {
  getSubscriptionStatus, openBillingPortal, refreshSubscription, getRefundNotice,
} from "../../services/subscriptionService";

/**
 * The account page: display name and plan, and nothing else.
 *
 * This replaces the two overlapping Velzon template pages (/profile and
 * /pages-profile-settings), which between them offered tabs of placeholder
 * data — experience, skills, a fake wallet balance — none of it wired to
 * anything real.
 */
const Profile = () => {
  document.title = "Profile | ForecastNest";

  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type, text }
  const [openingPortal, setOpeningPortal] = useState(false);
  const [refund, setRefund] = useState(null);      // a recent refund, or null

  /**
   * Hand the member over to Stripe to manage their subscription.
   *
   * The link is created on demand rather than stored: a portal session expires,
   * so a URL kept in the database would eventually send people to an error page
   * — which is what the Lemon Squeezy integration did.
   */
  const onManageBilling = async () => {
    setOpeningPortal(true);
    setFeedback(null);
    try {
      const res = await openBillingPortal();
      if (res && res.url) {
        window.location.href = res.url;
        return;
      }
      setFeedback({ type: "warning", text: "Could not open the billing portal." });
    } catch (e) {
      // The server's own words, and its code where the words alone would leave
      // the member without a next step. Before directApi this catch could only
      // ever show its own fallback: the shared client rejected with a string,
      // and a string has no .message.
      const detail =
        e?.code === "no_subscription"
          ? "There is no active subscription on this account to manage."
          : e?.code === "billing_not_configured" || e?.status === 503
            ? "Payments are not set up yet. Nothing has been charged — please try again later."
            : e?.message || "Could not open the billing portal.";
      setFeedback({
        type: "danger",
        text: e?.status ? `${detail} (${e.status})` : detail,
      });
    } finally {
      setOpeningPortal(false);
    }
  };

  const nameFrom = (user) => displayNameOf(user);

  // Stripe amounts are in the currency's smallest unit.
  const money = (amount, currency) => {
    if (typeof amount !== "number") return "";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency", currency: (currency || "usd").toUpperCase(),
      }).format(amount / 100);
    } catch {
      return `${(amount / 100).toFixed(2)} ${(currency || "").toUpperCase()}`.trim();
    }
  };

  // Dates from the API are ISO strings; an unparseable one must not render as
  // "Invalid Date" in a sentence about somebody's money.
  const onDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
  };

  useEffect(() => {
    (async () => {
      try {
        const user = await getProfile();
        setProfile(user);
        setDisplayName(nameFrom(user));
      } catch (e) {
        setFeedback({ type: "danger", text: "Could not load your profile." });
      }

      // Coming back from the customer portal, ask the server to re-read Stripe
      // first. The member may have just cancelled, and the webhook carrying
      // that news is a separate delivery — it arrives seconds later at best,
      // and not at all if the endpoint is misconfigured. Telling somebody who
      // has just cancelled that they renew next month is the worst version of
      // this page we could show them.
      if (new URLSearchParams(window.location.search).get("billing") === "updated") {
        try {
          await refreshSubscription();
        } catch {
          // Stale is survivable; the card below still renders from the mirror.
        }
        // Drop the marker so a reload does not re-read Stripe every time.
        window.history.replaceState({}, "", window.location.pathname);
      }

      // Billing is optional: until the subscriptions tables are migrated this
      // call fails, and that should read as "no plan yet", not as a broken page.
      try {
        setSubscription(await getSubscriptionStatus());
      } catch {
        setSubscription(null);
      }

      // Whether a refund was issued recently. Its own try/catch: this is an
      // extra on the page, and having no refunds is the normal case rather than
      // a failure.
      try {
        setRefund(await getRefundNotice());
      } catch {
        setRefund(null);
      }

      setLoading(false);
    })();
  }, []);


  const onSave = async () => {
    const name = displayName.trim().replace(/\s+/g, " ");
    if (!name) {
      setFeedback({ type: "warning", text: "Enter a display name first." });
      return;
    }
    // Said here as well as on the server, so the rule is visible before the
    // round trip rather than only as a refusal after it. The server is still
    // the one that decides — this cannot see whether a name is taken.
    if (!/^[A-Za-z0-9 ]+$/.test(name)) {
      setFeedback({
        type: "warning",
        text: "Use letters, numbers and spaces only \u2014 no punctuation or symbols.",
      });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const updated = await updateDisplayName(name);
      setProfile(updated);
      setDisplayName(nameFrom(updated));
      syncStoredUser(updated);
      setFeedback({ type: "success", text: "Display name updated." });
    } catch (e) {
      // directApi carries the server's own sentence, which is the point: "that
      // name is taken" and "use letters, numbers and spaces only" are different
      // problems with different fixes.
      setFeedback({
        type: "danger",
        text: e?.message || "Could not update your display name.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid className="text-center py-5">
          <Spinner /> <span className="ms-2">Loading your profile…</span>
        </Container>
      </div>
    );
  }

  const active = subscription?.active;
  const billingMode = subscription?.billing_mode || "off";

  // Cancelled, but still inside the paid period. Stripe leaves the status at
  // `active` and hangs a date on it, which is right — they paid for this
  // period and they keep it — but it means `active` on its own cannot be
  // read as "this is renewing".
  const cancelling = active && Boolean(subscription?.cancel_at_period_end);

  /**
   * Whether anybody may buy, decided by which keys the server loaded.
   *
   * Live keys mean a real card works, so checkout is the offer. Test keys mean
   * Stripe declines every real card, so a visitor who reached checkout would
   * hit a dead end; it stays admin-only there, which is exactly enough to test
   * the flow. No keys at all and there is nothing to show.
   *
   * Read from billing_mode rather than left as a condition to remember to
   * delete on the day the account goes live: putting the live keys in is the
   * switch, and there is only one of them.
   */
  const canCheckout = billingMode === "live"
    || (billingMode === "test" && Boolean(subscription?.admin));

  /**
   * A refund recent enough to still be worth reporting.
   *
   * Shown until it stops being news. The money takes five to ten business days
   * to appear on the card, and for that whole stretch this page is the only
   * evidence the member has that the refund happened — a subscription that has
   * simply vanished reads more like a loss than a refund.
   */
  const lastRefund = (() => {
    const r = refund?.last_refund;
    if (!r || !r.refunded_at) return null;
    const at = new Date(r.refunded_at);
    if (Number.isNaN(at.getTime())) return null;
    const days = (Date.now() - at.getTime()) / 86400000;
    return days <= (refund.notice_days || 30) ? { ...r, at } : null;
  })();
  const endsOn = onDate(subscription?.ends_at) || onDate(subscription?.renews_at);
  const renewsOn = onDate(subscription?.renews_at);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Profile" pageTitle="Account" />

        {feedback && (
          <Alert color={feedback.type} toggle={() => setFeedback(null)}>{feedback.text}</Alert>
        )}

        <Row>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Your details</h5>
              </CardHeader>
              <CardBody>
                <FormGroup>
                  <Label for="displayName">Display name</Label>
                  <Input id="displayName" value={displayName} maxLength={60}
                         placeholder="How your name appears in the app"
                         onChange={(e) => setDisplayName(e.target.value)} />
                  <span className="text-muted fs-12">
                    Letters, numbers and spaces, and it has to be unused.
                    Shown in the header and next to any research you share.
                    {realNameOf(profile) && (
                      <> Your account name with {profile?.provider === "google" ? "Google" : "us"} is{" "}
                      <strong>{realNameOf(profile)}</strong>; signing in again never changes
                      the name you set here.</>
                    )}
                  </span>
                </FormGroup>

                <FormGroup>
                  <Label>Email</Label>
                  <Input value={profile?.email || ""} readOnly disabled />
                  <span className="text-muted fs-12">
                    {profile?.provider === "google"
                      ? "Managed by your Google account."
                      : "Contact support to change your email."}
                  </span>
                </FormGroup>

                <Button color="primary" onClick={onSave}
                        disabled={saving || !displayName.trim() || displayName.trim() === nameFrom(profile)}>
                  {saving ? <><Spinner size="sm" /> Saving…</> : "Save"}
                </Button>
              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <h5 className="card-title mb-0">Your plan</h5>
                {cancelling ? (
                  <Badge color="warning">Cancelling</Badge>
                ) : active ? (
                  <Badge color="success">Active</Badge>
                ) : (
                  <Badge color="light" className="text-body">No plan</Badge>
                )}
              </CardHeader>
              <CardBody>
                {lastRefund && (
                  lastRefund.status === "failed" || lastRefund.status === "canceled" ? (
                    <Alert color="danger" className="py-2 px-3">
                      <span className="fw-medium">
                        {money(lastRefund.amount, lastRefund.currency)} could not be
                        returned to your card.
                      </span>{" "}
                      {lastRefund.failure_reason
                        ? `Your bank reported: ${lastRefund.failure_reason}.` : ""}{" "}
                      Please email{" "}
                      <a href="mailto:support@forecastnest.com">support@forecastnest.com</a>{" "}
                      and we will sort it out — you are still owed this money.
                    </Alert>
                  ) : (
                    <Alert color="success" className="py-2 px-3">
                      <span className="fw-medium">
                        {money(lastRefund.amount, lastRefund.currency)} refunded on{" "}
                        {onDate(lastRefund.at)}.
                      </span>{" "}
                      {lastRefund.status === "pending"
                        ? "It is on its way back to the card you paid with."
                        : "It has been sent back to the card you paid with."}{" "}
                      Card refunds usually appear within 5 to 10 business days — the
                      exact timing is up to your bank. It shows on your statement as a
                      credit from ForecastNest.
                    </Alert>
                  )
                )}

                {active ? (
                  <>
                    <p className="mb-2">
                      <strong className="text-capitalize">{subscription.plan || "Basic"}</strong>
                      {!cancelling && renewsOn && (
                        <span className="text-muted"> · renews {renewsOn}</span>
                      )}
                    </p>

                    {cancelling ? (
                      <Alert color="warning" className="mb-3 py-2 px-3">
                        <span className="fw-medium">This subscription is cancelled.</span>{" "}
                        {endsOn ? (
                          <>It will not renew, and your access continues until <strong>{endsOn}</strong>.</>
                        ) : (
                          <>It will not renew. Your access continues to the end of the period you have paid for.</>
                        )}
                        <br />
                        <span className="fs-12">
                          Changed your mind? Reopen billing below to start it again before then.
                        </span>
                      </Alert>
                    ) : (
                      <p className="text-muted mb-3">
                        Your monthly research allowance renews with your plan. Cancel at any
                        time from billing — you keep access until the period you have paid
                        for runs out.
                      </p>
                    )}
                    <div className="d-flex flex-wrap gap-2">
                      {/* No symbol to choose, and nothing to list. A
                          subscription used to buy the right to read our
                          forecasts for one symbol; it buys a larger monthly
                          research allowance now, and those forecasts are
                          internal. The "Research access: no symbol chosen yet"
                          line that used to sit above was the last of that model
                          still on screen, and it read as a step the member had
                          forgotten to finish. */}
                      {subscription.can_manage && (
                        <Button color={cancelling ? "primary" : "light"}
                                disabled={openingPortal} onClick={onManageBilling}>
                          {openingPortal
                            ? <><Spinner size="sm" /> Opening…</>
                            : cancelling ? "Manage or restart billing" : "Manage billing"}
                        </Button>
                      )}

                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-muted">
                      A free membership runs 15 research prompts a month.{" "}
                      <strong className="text-body">$9.99 a month</strong> raises that to
                      100, keeps up to ten private research strategies instead of one, and
                      lets you follow the standing experiments before they settle. Billed monthly, cancel any time. <Link to="/pricing">See what is included</Link>.
                    </p>
                    {billingMode === "live" ? (
                      <SubscribeButton label="Subscribe — $9.99/month" />
                    ) : (
                      // No checkout to offer yet, so ask instead. A subscribe
                      // button that opens a checkout which cannot complete is
                      // worse than no button.
                      <RequestAccessButton />
                    )}

                    {/*
                      The same checkout, kept reachable for an admin while the
                      account is still on test keys — Stripe declines real cards
                      there, so this must not be shown to anyone else.
                    */}
                    {canCheckout && billingMode !== "live" && (
                      <div className="mt-3 pt-3 border-top">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Badge color="warning">TEST</Badge>
                          <span className="text-muted fs-12">
                            Admin only · use card 4242 4242 4242 4242
                          </span>
                        </div>
                        <SubscribeButton
                          color="outline-primary"
                          label="Go to checkout"
                        />
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col><ResearchDisclaimer /></Col>
        </Row>
      </Container>
    </div>
  );
};

export default Profile;
